import { Box, TextField } from "@mui/material";
import { IResourceComponentsProps, useOne, useResource, useTranslate } from "@refinedev/core";
import { Create } from "@refinedev/mui";
import { useForm } from "@refinedev/react-hook-form";
import { useMemo } from "react";
import { useMatch } from "react-router";

import { CustomerProductsBreadcrumbs } from "@/components/breadcrumbs";

export const CustomerProductPermissionCreate: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();

  const { resource } = useResource("customer-product-permissions");
  const match = useMatch(`${resource?.create}`);
  const customer_id = match?.params.customer_id ?? "";
  const product_id = match?.params.product_id ?? "";
  const meta = useMemo(
    () => ({
      customer_id,
      product_id,
    }),
    [customer_id, product_id],
  );

  const { data: customer, isLoading: customerIsLoading } = useOne({
    resource: "customers",
    id: customer_id,
  });

  const { data: product, isLoading: productIsLoading } = useOne({
    resource: "products",
    id: product_id,
  });

  const {
    saveButtonProps,
    refineCore: { formLoading },
    register,
    formState: { errors },
  } = useForm({
    refineCoreProps: {
      resource: "customer-product-permissions",
      meta,
    },
  });

  const isPending = formLoading || customerIsLoading || productIsLoading;

  return (
    <Create
      breadcrumb={
        <CustomerProductsBreadcrumbs
          customer={customer?.data}
          product={product?.data}
          lastPaths={[
            {
              id: "customer-product-permissions",
              path: `/customers/${customer?.data.id}/products/${product?.data.id}/permissions`,
              title: translate("permissions.permissions", "Permissions"),
            },
          ]}
        />
      }
      isLoading={isPending}
      saveButtonProps={saveButtonProps}
    >
      <Box component="form" sx={{ display: "flex", flexDirection: "column" }} autoComplete="off">
        <TextField
          {...register("name", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.name}
          helperText={(errors as any)?.name?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label={translate("customer-product-permissions.fields.name")}
          name="name"
        />
        <TextField
          {...register("description", {
            required: false,
          })}
          error={!!(errors as any)?.description}
          helperText={(errors as any)?.description?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label={translate("customer-product-permissions.fields.description")}
          name="description"
        />
        <TextField
          {...register("expression", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.expression}
          helperText={(errors as any)?.expression?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label={translate("customer-product-permissions.fields.expression")}
          name="expression"
        />
        <TextField
          {...register("subject", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.subject}
          helperText={(errors as any)?.subject?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label={translate("customer-product-permissions.fields.subject")}
          name="subject"
        />
        <TextField
          {...register("subjectType", {
            required: "This field is required",
          })}
          error={!!(errors as any)?.subjectType}
          helperText={(errors as any)?.subjectType?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label={translate("customer-product-permissions.fields.subjectType")}
          name="subjectType"
        />
      </Box>
    </Create>
  );
};
